<script setup>
import InputError from '@/Components/InputError.vue'
import InputLabel from '@/Components/InputLabel.vue'
import PrimaryButton from '@/Components/PrimaryButton.vue'
import TextInput from '@/Components/TextInput.vue'
import StoreDemandController from '@/actions/App/Http/Controllers/StoreDemandController'
import { useForm } from '@inertiajs/vue3'
import { nextTick, ref } from 'vue'

defineProps({
    demand_types: {
        type: Array,
        required: true,
    },
    demand_of_constructions: {
        type: Array,
        required: true,
    },
})
const form = useForm({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',

    message: '',
    type_of_construction: '',
    square_meters: '',

    insulation_thickness: '',
    verification_code: '',
})

const successBanner = ref(null)
const errorBanner = ref(null)
const submitted = ref(false)
const failedAttempts = ref(0)

// Centre the element (the fixed header would cover a field scrolled to the
// top edge) and move focus there, so screen readers announce it too.
function reveal(element) {
    if (!element) {
        return
    }

    const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    element.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'center' })
    element.focus({ preventScroll: true })
}

// Field errors point at the first invalid field, `form` errors (expired page,
// network) have no field and point at the banner.
function revealFirstError() {
    nextTick(() => {
        const field = Object.keys(form.errors).find((key) => key !== 'form')

        reveal((field && document.getElementById(field.replaceAll('_', '-'))) || errorBanner.value)
    })
}

function submit() {
    let answered = false
    submitted.value = false

    form.post(StoreDemandController.url(), {
        preserveScroll: true,
        preserveState: true,

        onSuccess: () => {
            answered = true
            form.reset()
            submitted.value = true
            nextTick(() => reveal(successBanner.value))
        },
        onError: () => {
            answered = true
            failedAttempts.value++
            revealFirstError()
        },
        // A dropped connection fires neither onSuccess nor onError.
        onFinish: () => {
            if (answered) {
                return
            }

            form.setError('form', 'Nepodařilo se spojit se serverem. Zkontrolujte připojení a odešlete poptávku znovu.')
            failedAttempts.value++
            revealFirstError()
        },
    })
}
</script>

<template>
    <form
        name="demand-form"
        class="rounded-2xl bg-black/50 p-3 lg:flex-auto"
        @submit.prevent="submit"
    >
        <div
            role="status"
            aria-live="polite"
        >
            <p
                v-if="submitted"
                ref="successBanner"
                tabindex="-1"
                class="mb-4 rounded-lg bg-green-50 p-3 text-sm font-medium text-green-800 focus:outline-none"
            >
                Děkujeme, poptávku jsme přijali. Ozveme se vám co nejdříve.
            </p>
        </div>

        <div role="alert">
            <div
                v-if="form.hasErrors"
                :key="failedAttempts"
                ref="errorBanner"
                tabindex="-1"
                class="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700 focus:outline-none"
            >
                <p class="font-medium">Poptávku se nepodařilo odeslat.</p>
                <p>{{ form.errors.form ?? 'Zkontrolujte prosím chyby u jednotlivých polí.' }}</p>
            </div>
        </div>

        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
                <InputLabel
                    value="Jméno"
                    for="first-name"
                    isRequired
                />

                <TextInput
                    id="first-name"
                    name="first-name"
                    autocomplete="given-name"
                    placeholder="Petr"
                    class="mt-1"
                    v-model="form.first_name"
                />

                <InputError
                    :message="form.errors.first_name"
                    class="mt-1"
                />
            </div>

            <div>
                <InputLabel
                    value="Příjmení"
                    for="last-name"
                    isRequired
                />

                <TextInput
                    id="last-name"
                    name="last-name"
                    autocomplete="family-name"
                    placeholder="Novák"
                    class="mt-1"
                    v-model="form.last_name"
                />

                <InputError
                    :message="form.errors.last_name"
                    class="mt-1"
                />
            </div>

            <div>
                <InputLabel
                    value="Email"
                    for="email"
                    isRequired
                />

                <TextInput
                    id="email"
                    name="email"
                    autocomplete="email"
                    placeholder="petrnovak@seznam.cz"
                    class="mt-1 w-full"
                    v-model="form.email"
                    icon="email"
                />

                <InputError
                    :message="form.errors.email"
                    class="mt-1"
                />
            </div>

            <div>
                <InputLabel
                    value="Mobilní číslo"
                    for="phone"
                />

                <TextInput
                    id="phone"
                    name="phone"
                    autocomplete="tel"
                    placeholder="+420 123 456 789"
                    class="mt-1 w-full"
                    v-model="form.phone"
                    icon="phone"
                />

                <InputError
                    :message="form.errors.phone"
                    class="mt-1"
                />
            </div>

            <div class="col-span-full">
                <InputLabel
                    value="Město"
                    for="city"
                />

                <TextInput
                    id="city"
                    name="city"
                    placeholder="Praha"
                    class="mt-1 w-full"
                    v-model="form.city"
                />

                <InputError
                    :message="form.errors.city"
                    class="mt-1"
                />
            </div>

            <div>
                <InputLabel
                    value="Adresa"
                    for="address"
                />

                <TextInput
                    id="address"
                    name="address"
                    placeholder="Ulice 1234/56"
                    class="mt-1 w-full"
                    v-model="form.address"
                />

                <InputError
                    :message="form.errors.address"
                    class="mt-1"
                />
            </div>
            <div>
                <InputLabel
                    value="PSČ"
                    for="postal-code"
                />

                <TextInput
                    id="postal-code"
                    name="postal-code"
                    placeholder="123 45"
                    class="mt-1 w-full"
                    v-model="form.postal_code"
                />

                <InputError
                    :message="form.errors.postal_code"
                    class="mt-1"
                />
            </div>

            <hr class="col-span-full my-4 border-gray-300" />

            <div class="col-span-full">
                <InputLabel
                    value="Typ konstrukce"
                    isRequired
                />

                <div
                    id="type-of-construction"
                    tabindex="-1"
                    class="mt-1 grid gap-3 rounded-xl bg-white p-2 focus:outline-none md:grid-cols-3"
                >
                    <div
                        v-for="type in demand_of_constructions"
                        :key="type.value"
                    >
                        <button
                            type="button"
                            name="type_of_construction"
                            @click="form.type_of_construction = type.value"
                            :aria-label="'Vybrat typ konstrukce' + type.label"
                            :class="[
                                form.type_of_construction === type.value
                                    ? 'ring-brand-orange bg-brand-orange'
                                    : 'hover:ring-brand-orange ring-transparent hover:bg-gray-100',
                                'relative flex w-full flex-col items-center justify-between overflow-hidden rounded-md text-sm font-medium text-gray-900 ring-4',
                            ]"
                        >
                            <img
                                :src="type.image_path"
                                :alt="'Typ poptávka ' + type.label"
                                :class="[
                                    form.type_of_construction === type.value ? 'opacity-100' : 'opacity-50',
                                    'size-50 object-cover lg:w-full',
                                ]"
                            />

                            <span class="absolute bottom-0 w-full bg-black/20 p-2 text-xl text-white backdrop-blur-lg">
                                {{ type.label }}
                            </span>
                        </button>
                    </div>
                </div>

                <InputError
                    :message="form.errors.type_of_construction"
                    class="mt-1"
                />
            </div>

            <div class="col-span-full">
                <InputLabel
                    value="Počet m² (v metrech čtverečních)"
                    for="square-meters"
                />

                <TextInput
                    id="square-meters"
                    name="square-meters"
                    placeholder="100"
                    class="mt-1 w-full"
                    suffix="m²"
                    v-model="form.square_meters"
                />

                <InputError
                    :message="form.errors.square_meters"
                    class="mt-1"
                />
            </div>

            <div
                class="col-span-full"
                v-if="form.type_of_construction === 'blown_insulation'"
            >
                <InputLabel
                    value="Tloušťku zateplení (v milimetrech)"
                    for="insulation-thickness"
                    isRequired
                />

                <TextInput
                    id="insulation-thickness"
                    name="insulation-thickness"
                    placeholder="10"
                    suffix="mm"
                    type="number"
                    class="mt-1 w-full"
                    v-model="form.insulation_thickness"
                />

                <InputError
                    :message="form.errors.insulation_thickness"
                    class="mt-1"
                />
            </div>

            <div class="sm:col-span-2">
                <InputLabel
                    value="Zpráva"
                    for="message"
                />

                <div class="mt-1">
                    <textarea
                        id="message"
                        name="message"
                        v-model="form.message"
                        placeholder="Chtěl bych se zeptat na ..."
                        rows="4"
                        class="focus:outline-brand-orange block w-full rounded-md bg-white px-3.5 py-2 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2"
                    />
                </div>

                <InputError
                    :message="form.errors.message"
                    class="mt-1"
                />
            </div>

            <div class="col-span-full">
                <InputLabel
                    value="Ověřovací kód - (Zadejte aktuální rok)"
                    for="verification-code"
                    isRequired
                />

                <TextInput
                    id="verification-code"
                    name="verification-code"
                    placeholder="Jaký je aktuální rok?"
                    class="mt-1 w-full"
                    v-model="form.verification_code"
                    icon="verification"
                />

                <InputError
                    :message="form.errors.verification_code"
                    class="mt-1"
                />
            </div>
        </div>

        <div class="mt-10">
            <PrimaryButton
                :class="{ 'opacity-25': form.processing }"
                :disabled="form.processing || form.recentlySuccessful"
                type="submit"
                :success="form.recentlySuccessful"
            >
                {{ form.processing ? 'Odesílám...' : 'Odeslat' }}
            </PrimaryButton>
        </div>
    </form>
</template>
